import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import Table from '../components/ui/Table';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import SearchableCombobox from '../components/ui/SearchableCombobox';
import SearchInput from '../components/ui/SearchInput';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Pagination from '../components/ui/Pagination';
import { Plus, Edit, Trash2, User, Mail, Lock, Phone, Building, Shield, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUIStore } from '../store/useUIStore';

const UserManagement = () => {
  console.log('UserManagement Component Loaded - Version 3.0');
  const { user } = useAuth();
  const { settings } = useSettings();
  const isEfileEnabled = settings?.efile_enabled === '1' || settings?.efile_enabled === 1 || settings?.efile_enabled === true || settings?.efile_enabled === 'true';
  const location = useLocation();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [offices, setOffices] = useState([]);
  
  // Hierarchy States
  const [wings, setWings] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sections, setSections] = useState([]);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { get, post, put, patch } = useApi();
  const { tableDensity, setTableDensity } = useUIStore();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: settings.user_roles?.[0] || 'Clerk',
    department: '',
    wingId: '',
    departmentId: '',
    sectionId: '',
    designation: '',
    scale: '',
    officialAddress: '',
    phone: '',
    password: '',
    officeId: '',
    efileCanInitiate: true,
    efileCanForward: true,
    efileCanApprove: false
  });

  useEffect(() => {
    fetchWings();
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    if (searchParam) {
        setSearch(searchParam);
    }
  }, [location.search]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchWings = async () => {
    try {
      const response = await get('/hierarchy/wings');
      if (response.success) setWings(response.data);
    } catch (error) {
      console.error('Failed to fetch wings', error);
      toast.error('Failed to load wings');
    }
  };

  const fetchDepartments = async (wingId) => {
    if (!wingId) {
        setDepartments([]);
        return;
    }
    try {
      const response = await get(`/hierarchy/wings/${wingId}/departments`);
      if (response.success) setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments', error);
      toast.error('Failed to load departments');
    }
  };

  const fetchSections = async (departmentId) => {
    if (!departmentId) {
        setSections([]);
        return;
    }
    try {
      const response = await get(`/hierarchy/departments/${departmentId}/sections`);
      if (response.success) setSections(response.data);
    } catch (error) {
      console.error('Failed to fetch sections', error);
      toast.error('Failed to load sections');
    }
  };

  // When editing, we need to pre-fetch the departments and sections
  useEffect(() => {
    if (selectedUser) {
        if (selectedUser.wingId) fetchDepartments(selectedUser.wingId);
        if (selectedUser.departmentId) fetchSections(selectedUser.departmentId);
    }
  }, [selectedUser]);

  useEffect(() => {
    fetchUsers();
    if (user?.role === 'SuperAdmin') {
      fetchOffices();
    }
  }, [sortConfig, debouncedSearch, page]);

  const fetchOffices = async () => {
    try {
      const response = await get('/offices');
      setOffices(response.data);
    } catch (error) {
      console.error('Failed to fetch offices', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await get(`/users?page=${page}&limit=${limit}&sortBy=${sortConfig.key}&order=${sortConfig.direction}&search=${debouncedSearch}`);
      const rows = Array.isArray(response?.data) ? response.data : [];
      setUsers(rows);
      if (response?.pagination) {
        setTotalPages(Number(response.pagination.totalPages || 1));
        setTotalUsers(Number(response.pagination.total || rows.length));
      } else {
        setTotalPages(Math.max(1, Math.ceil(rows.length / limit)));
        setTotalUsers(rows.length);
      }
    } catch (error) {
      console.error('Failed to fetch users', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      // Default to current user's office if not specified (for non-SuperAdmins or if skipped)
      if (!payload.officeId) payload.officeId = user.officeId;

      if (selectedUser) {
        // Update
        if (!payload.password) delete payload.password; // Don't send empty password
        await put(`/users/${selectedUser.id}`, payload);
        toast.success('User updated successfully');
      } else {
        // Create
        await post('/users', payload);
        toast.success('User created successfully');
      }
      fetchUsers();
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const openSetPassword = () => {
    if (!selectedUser) return;
    setPasswordTarget(selectedUser);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (!passwordTarget?.id) return;
    if (passwordSubmitting) return;
    if (String(newPassword || '').length < 6) {
      toast.error('Password min 6 chars');
      return;
    }
    setPasswordSubmitting(true);
    try {
      await put(`/users/${passwordTarget.id}/set-password`, { newPassword });
      toast.success('Password updated');
      setShowPasswordModal(false);
      setPasswordTarget(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update password');
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedUser) return;
    
    try {
      await patch(`/users/${selectedUser.id}/toggle-active`);
      toast.success(`User ${selectedUser.isActive ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
      setIsDeleteOpen(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const openAddUserModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      role: settings.user_roles?.[0] || 'Clerk',
      department: '',
      wingId: '',
      departmentId: '',
      sectionId: '',
      designation: '',
      scale: '',
      officialAddress: '',
      phone: '',
      password: '',
      officeId: '',
      efileCanInitiate: true,
      efileCanForward: true,
      efileCanApprove: false
    });
    setSelectedUser(null);
  };

  const handleSort = (key) => {
    setPage(1);
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const columns = [
    { header: 'Name', accessorKey: 'name', sortable: true },
    { header: 'Email', accessorKey: 'email', className: 'text-gray-500' },
    { header: 'Role', accessorKey: 'role', 
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          row.role === 'SuperAdmin' ? 'bg-purple-100 text-purple-700' :
          row.role === 'Admin' ? 'bg-blue-100 text-blue-700' :
          'bg-gray-100 text-gray-700'
        }`}>
          {row.role}
        </span>
      )
    },
    { header: 'Department', accessorKey: 'department', className: 'text-gray-500' },
    { header: 'Designation', accessorKey: 'designation', className: 'text-gray-500' },
    ...(isEfileEnabled ? [
      { header: 'Scale', accessorKey: 'scale', className: 'text-gray-500' },
      { header: 'Official Address', accessorKey: 'officialAddress', className: 'text-gray-500' }
    ] : []),
    { header: 'Office', accessorKey: 'officeId',
      render: (row) => {
        const office = offices.find(o => o.id === row.officeId);
        return <span>{office ? office.name : '-'}</span>;
      }
    },
    { 
      header: 'Status', 
      accessorKey: 'isActive', 
      sortable: true,
      render: (row) => (
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
          row.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ) 
    },
    { 
      header: 'Actions', 
      accessorKey: 'actions', 
      render: (row) => (
        <div className="flex space-x-2">
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/profile/${row.id}`); }}>
            <Edit className="w-4 h-4 text-blue-600" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            className={row.isActive ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'} 
            onClick={(e) => { e.stopPropagation(); setSelectedUser(row); setIsDeleteOpen(true); }}
          >
            {row.isActive ? <Trash2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />} 
          </Button>
        </div>
      )
    },
  ];

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">User Management</h1>
          <p className="text-gray-500 mt-1 text-lg">Manage system users, roles, and permissions</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            <Button
              size="sm"
              variant={tableDensity === 'comfortable' ? 'primary' : 'ghost'}
              onClick={() => setTableDensity('comfortable')}
            >
              Comfortable
            </Button>
            <Button
              size="sm"
              variant={tableDensity === 'compact' ? 'primary' : 'ghost'}
              onClick={() => setTableDensity('compact')}
            >
              Compact
            </Button>
          </div>
          <Button onClick={() => openAddUserModal()}>
            <Plus className="w-5 h-5 mr-2" />
            Add New User
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <SearchInput
          value={search}
          onChange={setSearch}
          onClear={() => setSearch('')}
          placeholder="Search users by name, email, role or department..."
          className="border-gray-200 focus:ring-indigo-500"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <Table 
          columns={columns} 
          data={users} 
          sortConfig={sortConfig}
          onSort={handleSort}
          density={tableDensity}
          onRowClick={(row) => {
            console.log('Row clicked:', row);
            if (row && row.id) {
              navigate(`/profile/${row.id}`);
            } else {
              console.error('Invalid row or missing ID:', row);
              toast.error('Cannot open profile: User ID missing');
            }
          }}
        />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={totalUsers}
          itemsPerPage={limit}
        />
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title={selectedUser ? 'Edit User' : 'Add New User'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input 
            label="Full Name"
            name="name"
            icon={User}
            required 
            value={formData.name}
            onChange={handleChange}
            placeholder="John Doe"
          />
          
          <Input 
            label="Email Address"
            type="email" 
            name="email"
            icon={Mail}
            required 
            value={formData.email}
            onChange={handleChange}
            disabled={!!selectedUser}
            placeholder="john@example.com"
          />

          {!selectedUser && (
            <Input 
              label="Password"
              type="password" 
              name="password"
              icon={Lock}
              required 
              value={formData.password}
              onChange={handleChange}
              placeholder="Min. 6 characters"
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            {user?.role === 'SuperAdmin' && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Office</label>
                <SearchableCombobox 
                  items={offices.map(o => ({ label: o.name, value: o.id }))}
                  value={formData.officeId}
                  onChange={(val) => setFormData(prev => ({ ...prev, officeId: val }))}
                  placeholder="Select Office"
                />
              </div>
            )}
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <SearchableCombobox 
                items={Array.from(new Set([
                  ...(settings.user_roles || []), 
                  'SuperAdmin', 'Admin', 'Officer', 'Clerk'
                ])).map(r => ({ label: r, value: r }))}
                value={formData.role}
                onChange={(val) => setFormData(prev => ({ ...prev, role: val }))}
                placeholder="Select Role"
              />
            </div>

            {isEfileEnabled && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">e-File Permissions</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.efileCanInitiate)}
                      onChange={(e) => setFormData(prev => ({ ...prev, efileCanInitiate: e.target.checked }))}
                    />
                    Initiator
                  </label>
                  <label className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.efileCanForward)}
                      onChange={(e) => setFormData(prev => ({ ...prev, efileCanForward: e.target.checked }))}
                    />
                    Forwarder
                  </label>
                  <label className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.efileCanApprove)}
                      onChange={(e) => setFormData(prev => ({ ...prev, efileCanApprove: e.target.checked }))}
                    />
                    Approver
                  </label>
                </div>
              </div>
            )}

            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Wing</label>
              <SearchableCombobox 
                items={wings.map(w => ({ label: w.name, value: w.id }))}
                value={formData.wingId}
                onChange={(val) => {
                  setFormData(prev => ({ ...prev, wingId: val, departmentId: '', sectionId: '' }));
                  fetchDepartments(val);
                }}
                placeholder="Select Wing"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <SearchableCombobox 
                items={departments.map(d => ({ label: d.name, value: d.id }))}
                value={formData.departmentId}
                onChange={(val) => {
                  setFormData(prev => ({ ...prev, departmentId: val, sectionId: '' }));
                  fetchSections(val);
                }}
                disabled={!formData.wingId}
                placeholder="Select Department"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
              <SearchableCombobox 
                items={sections.map(s => ({ label: s.name, value: s.id }))}
                value={formData.sectionId}
                onChange={(val) => setFormData(prev => ({ ...prev, sectionId: val }))}
                disabled={!formData.departmentId}
                placeholder="Select Section"
              />
            </div>

            <Input 
              label="Designation"
              name="designation"
              icon={User}
              value={formData.designation}
              onChange={handleChange}
              placeholder="e.g. Senior Officer"
            />
            {isEfileEnabled && (
              <Input 
                label="Scale"
                name="scale"
                icon={Shield}
                value={formData.scale}
                onChange={handleChange}
                placeholder="e.g. BPS-17"
              />
            )}
          </div>

          {isEfileEnabled && (
            <Input 
              label="Official Address"
              name="officialAddress"
              icon={Building}
              value={formData.officialAddress}
              onChange={handleChange}
              placeholder="Room 101, Building A"
            />
          )}

          <Input 
            label="Phone Number"
            name="phone"
            icon={Phone}
            value={formData.phone}
            onChange={handleChange}
            placeholder="+1 234 567 890"
          />

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            {selectedUser && user?.role === 'SuperAdmin' && (
              <Button type="button" variant="outline" onClick={openSetPassword}>
                Set Password
              </Button>
            )}
            <Button type="submit">
              {selectedUser ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title={`Set Password${passwordTarget?.name ? `: ${passwordTarget.name}` : ''}`}
      >
        <form onSubmit={handleSetPassword} className="space-y-4">
          <Input
            label="New Password"
            type="password"
            icon={Lock}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Min. 6 characters"
          />
          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={() => setShowPasswordModal(false)} disabled={passwordSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={passwordSubmitting}>
              Update Password
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleToggleStatus}
        title={selectedUser?.isActive ? "Deactivate User" : "Activate User"}
        message={`Are you sure you want to ${selectedUser?.isActive ? 'deactivate' : 'activate'} ${selectedUser?.name}? ${selectedUser?.isActive ? 'They will no longer be able to log in.' : 'They will regain access to the system.'}`}
        confirmText={selectedUser?.isActive ? "Deactivate" : "Activate"}
        variant={selectedUser?.isActive ? "danger" : "primary"}
      />
    </>
  );
};

export default UserManagement;
